import {useEffect, useState} from 'react';
import {t} from 'i18next';
import {useDispatch} from 'react-redux';
import {Box, Typography} from '@mui/material';
import {ReactComponent as InvestigativeIcon} from '../../../../assests/svg/investigative.svg';
import {
  getClientById,
  getProjectByProjectId,
} from '../../../../api/ClientManagementService';
import ProjectOverView from '../../welcome-screen-single-client/ProjectOverView';
import {Project, ProjectStatus} from '../../welcome-screen-single-client/model';
import AurCountryFlag from '../../../common/aur-country-flag/AurCountryFlag';
import AurIcon, {IconNames} from '../../../common/aur-icon/AurIcon';
import {setClientData} from '../../../../redux/slices/selectedClientSlice';
import usePermissionsByProjectId from '../../../../hooks/permissions/usePermissionsByProjectId';
import {
  GA_EVENT_NAMES,
  GA_SUBPAGE_TYPE,
} from '../../../../constants/analyticsConstants';
import {useAnalytics} from '../../../../hooks/useAnalytics';

export interface IProjectDetailsProps {
  projectId: string;
  alphaCode: string;
  preferredId: string;
  columnCount?: number;
}

const ProjectDetails: React.FC<IProjectDetailsProps> = ({
  projectId,
  alphaCode,
  preferredId,
  columnCount,
}) => {
  const [detailPanelHeader, setDetailPanelHeader] = useState(<></>);
  const [detailPanelContent, setDetailPanelContent] = useState(<></>);

  const dispatch = useDispatch();
  const {trackGAEvent} = useAnalytics();

  const {getPermissionsByClientIdProjectId} =
    usePermissionsByProjectId(projectId);

  useEffect(() => {
    if (!projectId) {
      return;
    }
    loadProjectDetails(projectId);
  }, [projectId]);

  const loadProjectDetails = async (projectId: string) => {
    // Get project by ID
    const projectResponse = await getProjectByProjectId(projectId);
    const project = projectResponse?.data?.data;
    const clientId = project.clientId;
    trackGAEvent({
      eventName: GA_EVENT_NAMES.SUB_PAGE_VIEW,
      subPage: {
        subpage_name: t('gaTracking.welcomeScreen.projectView'),
        subpage_type: GA_SUBPAGE_TYPE.ACCORDION,
      },
      project: {
        id: projectId,
        client_id: project.clientId,
        alpha_code: alphaCode,
        type: project.projectType,
        branch_id: project.branchId,
        department_id: project.departmentId,
        vertical: project.vertical,
      },
      hasProjectData: true,
    });

    // Get client by ID
    const clientResponse = await getClientById(clientId);
    const client = clientResponse?.data?.data;

    // Set some global variables in redux
    dispatch(setClientData(client));
    await getPermissionsByClientIdProjectId();

    // Reformat the API object to the ProjectOverView object
    const projectDetails: Project = {
      ...project,
      projectAlphaCode: alphaCode,
      clientPreferredId: preferredId,
      nlpSearchEnabled: project.nplSearchEnabled,
      clientBranch: {
        name: project.branchName,
      },
      clientDepartment: {
        name: project.departmentName,
      },
      workspaces: project.workspaces.map((i: any) => {
        return {
          ...i,
          name: i.workspaceName,
        };
      }),
    };

    loadDetailPanelHeader(projectDetails);
    loadDetailPanelContent(projectDetails);
  };

  const loadDetailPanelContent = (projectDetails: Project) => {
    const isProjectClosed =
      projectDetails.projectStatus == ProjectStatus.closed;

    setDetailPanelContent(
      <ProjectOverView
        projectDetails={projectDetails}
        isProjectClosed={isProjectClosed}
      />
    );
  };

  const loadDetailPanelHeader = (projectDetails: Project) => {
    const isProjectClosed =
      projectDetails.projectStatus == ProjectStatus.closed;

    setDetailPanelHeader(
      <>
        <Box
          className={`${isProjectClosed ? 'closed mist-700 ' : ''} d-inline-flex`}
        >
          <Typography className="typo-text subhead1_size" component="div">
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                whiteSpace: 'pre',
              }}
            >
              {projectDetails.projectName}
              {projectDetails.ecaEnabled && (
                <AurCountryFlag
                  countryCode={projectDetails?.domainCountryCode ?? ''}
                  altText={projectDetails?.countryName ?? ''}
                />
              )}
            </Box>
          </Typography>

          {isProjectClosed && (
            <Box className="closed-chip">
              <AurIcon icon={IconNames.Block} size="small" />{' '}
              <Typography className="typo-text body2_size" component="span">
                {t('welcomeScreenSingleClient.closed')}
              </Typography>
            </Box>
          )}
        </Box>

        {projectDetails.ecaEnabled && (
          <Box className="eca-enable-wrapper">
            <Typography
              className="typo-text subhead3_size eca-enabled"
              component="span"
            >
              {t('welcomeScreenSingleClient.ecaEnabled')}
            </Typography>
            {projectDetails.nlpSearchEnabled ? (
              <>
                <InvestigativeIcon />
                <Typography
                  className="typo-text body1_size investigative-search"
                  component="span"
                >
                  {t('welcomeScreenSingleClient.withInvestigativeSearch')}
                </Typography>
              </>
            ) : (
              <Typography
                className="typo-text body1_size investigative-search"
                component="span"
              >
                {t('welcomeScreenSingleClient.withoutInvestigativeSearch')}
              </Typography>
            )}
          </Box>
        )}
      </>
    );
  };

  return (
    <div className="welcome-screen-single-client" role="row">
      <div
        className="single-client-content"
        role="gridcell"
        aria-colspan={columnCount}
        tabIndex={-1}
      >
        <div className="detail-panel-header">{detailPanelHeader}</div>
        <div className="detail-panel-content">{detailPanelContent}</div>
      </div>
    </div>
  );
};

export default ProjectDetails;
